import { useState, useRef, useCallback, useEffect } from "react";
import { handlePostApiGF, Toast } from "../Utils/general-function";
import { RazorpayScriptLoad } from "./RazorpayScriptLoad";

const PAYMENT_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  OPENED: "opened",
  VERIFYING: "verifying",
  SUCCESS: "success",
  FAILED: "failed",
  CANCELLED: "cancelled",
  PENDING: "pending",
};

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

const STORAGE_KEY = "pending_payment"; // use to store  razorpay reponse when backend failed to verify payment id

const useRazorpayPayment = () => {
  const [status, setStatus] = useState(PAYMENT_STATUS.IDLE);

  const isVerifyingRef = useRef(false);
  const isVerifiedSuccessRef = useRef(false);
  const statusRef = useRef(status);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  //2. 🔁 VERIFY (only once properly)
  const verifyPayment = useCallback(async (response) => {
    if (isVerifiedSuccessRef.current || isVerifyingRef.current) return; // if payment success or verification in process

    try {
      const { razorpay_order_id, razorpay_payment_id, razorpay_signature } =
        response || {};

      if (!razorpay_order_id || !razorpay_payment_id || !razorpay_signature) {
        setStatus(PAYMENT_STATUS.FAILED);
        throw new Error("Invalid payment response");
      }

      isVerifyingRef.current = true;
      setStatus(PAYMENT_STATUS.VERIFYING);

      const payload = {
        bOrderId: razorpay_order_id,
        bTransactionId: razorpay_payment_id,
        signature: razorpay_signature,
      };

      for (let i = 1; i <= MAX_RETRIES; i++) {
        try {
          if (isVerifiedSuccessRef.current) return;

          const res = await handlePostApiGF({
            url: "/api/WalletTransaction/Verify",
            obj: payload,
          });

          if (res?.succ) {
            isVerifiedSuccessRef.current = true;

            sessionStorage.removeItem(STORAGE_KEY);

            setStatus(PAYMENT_STATUS.SUCCESS);
            Toast(true, res?.msg || "Payment successful!", "");
            return;
          }

          if (i < MAX_RETRIES) await sleep(RETRY_DELAY);
        } catch {
          if (i < MAX_RETRIES) await sleep(RETRY_DELAY);
        }
      }

      // fallback
      setStatus(PAYMENT_STATUS.PENDING);

      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(response));

      Toast(false, "", "Payment processing...");
    } catch (err) {
      console.error("Error verifying payment:", err);
      setStatus(PAYMENT_STATUS.PENDING);
      Toast(false, "", "Verification delayed. It will update shortly.");
    } finally {
      isVerifyingRef.current = false;
    }
  }, []);

  //3. 🔁 RECOVERY AFTER REFRESH
  const retryPayment = useCallback(async () => {
    if (isVerifiedSuccessRef.current) return;
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      Toast(false, "", "No pending payment found.");
      sessionStorage.removeItem(STORAGE_KEY);
      setStatus(PAYMENT_STATUS.IDLE);
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(stored);
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
      setStatus(PAYMENT_STATUS.FAILED);
      return;
    }

    try {
      await verifyPayment(parsed);
    } catch {
      setStatus(PAYMENT_STATUS.PENDING);
    }
  }, [verifyPayment]);

  //1. 🚀 PAYMENT START
  const handlePayment = useCallback(
    async ({ amount, order_id }) => {
      // MAIN FUNCTION TO START PAYMENT
      if (!amount || amount <= 0 || !order_id) return;

      if (
        statusRef.current === PAYMENT_STATUS.LOADING ||
        statusRef.current === PAYMENT_STATUS.OPENED ||
        statusRef.current === PAYMENT_STATUS.VERIFYING
      ) {
        return;
      }

      isVerifiedSuccessRef.current = false;
      isVerifyingRef.current = false;
      setStatus(PAYMENT_STATUS.LOADING);
      const script = await RazorpayScriptLoad(); // load razorpay script
      if (!script || !window.Razorpay) {
        setStatus(PAYMENT_STATUS.FAILED);
        Toast(false, "", "Payment system failed to load");
        return;
      }

      const rzp = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: Math.round(amount * 100),
        currency: "INR",
        order_id,
        handler: async (response) => {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(response));
          console.log("response succ", response);

          // return;
          await verifyPayment(response);
        },
        modal: {
          ondismiss: () => {
            if (statusRef.current !== PAYMENT_STATUS.SUCCESS) {
              setStatus(PAYMENT_STATUS.CANCELLED);
              Toast(false, "", "Payment cancelled");
            }
          },
        },
        image: "https://m.carthi.in/assets/images/logo.png",
        name: "KAPURIYA AUTOMOTIVE SERVICES PRIVATE LIMITED",
        theme: {
          color: "#ff0000",
        },
      });

      rzp.on("payment.failed", () => {
        setStatus(PAYMENT_STATUS.FAILED);
        Toast(false, "", "Payment failed. Please try again.");
      });

      rzp.open();
      setStatus(PAYMENT_STATUS.OPENED);
    },
    [verifyPayment],
  );

  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      setStatus(PAYMENT_STATUS.PENDING);
    }
  }, []);

  return { status, handlePayment, PAYMENT_STATUS, retryPayment };
};

export default useRazorpayPayment;

//OLD CODE OF MINE
// // order_SaBNnAsJyeYIHu
// // rzp_test_MuW12ePvQ8Q5BH

// import { useState } from "react";
// import { handlePostApiGF, Toast } from "../Utils/general-function";
// import { RazorpayScriptLoad } from "./RazorpayScriptLoad";

// const useRazorpayPayment = () => {
//   const [status, setStatus] = useState("idle");

//   const verifyPayment = async (paymentResponse) => {
//     // backend verification of payment response
//     const { razorpay_order_id, razorpay_payment_id, razorpay_signature } =
//       paymentResponse || {};

//     if (!razorpay_order_id || !razorpay_payment_id || !razorpay_signature) {
//       Toast(false, "", "Invalid payment response. Please try again.");
//       setStatus("failed");
//       return;
//     }

//     let obj = {
//       bOrderId: razorpay_order_id,
//       bTransactionId: razorpay_payment_id,
//       signature: razorpay_signature,
//     };

//     try {
//       const verifyResponse = await handlePostApiGF({
//         url: "/api/WalletTransaction/Verify",
//         obj,
//       });
//       console.log("verifyResponse", verifyResponse);
//       if (!verifyResponse?.succ) {
//         Toast(
//           false,
//           "",
//           verifyResponse?.msg ||
//             "Backend Payment verification failed. Please try again.",
//         );
//         setStatus("pending");

//         return;
//       } else {
//         Toast(
//           true,
//           verifyResponse?.msg || "Payment verified successfully!",
//           "",
//         );
//         setStatus("success");
//         return true;
//       }
//     } catch (error) {
//       console.error("Error verifying payment:", error);
//       Toast(false, "", "Backend Failed to verify payment. Please try again.");
//       setStatus("failed");
//       return;
//     }
//   };

//   const handlePayment = async ({ amount, order_id }) => {
//     let razorpayScript = await RazorpayScriptLoad();
//     if (!razorpayScript) {
//       Toast(
//         false,
//         "",
//         "Razorpay SDK failed to load. Please check your connection.",
//       );
//       setStatus("failed");
//       return;
//     }
//     if (!amount || !order_id) {
//       Toast(false, "", "Invalid payment details. Please try again.");
//       setStatus("failed");
//       return;
//     }
//     setStatus("loading");

//     try {
//       const options = {
//         key: "rzp_test_MuW12ePvQ8Q5BH",
//         amount: amount * 100,
//         currency: "INR",
//         // order_id,
//         order_id: "order_SdMMusUROYq0Nb",
//         // ✅ SUCCESS
//         handler: async function (response) {
//           // call when razorpay payment is successful
//           setStatus("verifying");
//           console.log("response on success handler", response);

//           const verifyPay = await verifyPayment(response);
//           console.log("verifyPay", verifyPay);
//           return verifyPay;
//         },

//         // ❌ CANCEL
//         modal: {
//           ondismiss: function (err) {
//             // call when user closes the payment form without completing payment
//             console.error("Payment cancelled:", err);
//             Toast(false, "", "Payment cancelled by user.");
//             setStatus("cancelled");
//             return;
//           },
//         },
//       };

//       if (!window.Razorpay) {
//         Toast(
//           false,
//           "",
//           "Razorpay SDK failed to load. Please check your connection.",
//         );
//         setStatus("failed");
//         return;
//       }
//       const rzp = new window.Razorpay(options);
//       rzp.open();

//       // ❌ FAILURE
//       rzp.on("payment.failed", function (err) {
//         // call when payment fails due to any reason
//         console.error(
//           "Payment failedd:",
//           err?.error?.description || "Payment failed",
//         );
//         Toast(false, "", err?.error?.description || "Payment failed");
//         setStatus("failed");
//         return;
//       });
//     } catch (err) {
//       console.error("An error occurred:", err);
//       setStatus("failed");
//       return;
//     }
//   };

//   return {
//     status,
//     setStatus,
//     handlePayment,
//   };
// };

// export default useRazorpayPayment;
